// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Crews CRUD
// Crew management with contractor/zone assignments
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid,
} from '@mantine/core';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { DebrisCrew, CrewStatus } from '../../types/debris';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'standby', label: 'Standby' },
  { value: 'out_of_service', label: 'Out of Service' },
];

const CREW_TYPES = [
  { value: 'knuckleboom', label: 'Knuckleboom' },
  { value: 'grapple', label: 'Grapple' },
  { value: 'self_loader', label: 'Self-Loader' },
  { value: 'dump_truck', label: 'Dump Truck' },
];

const STATUS_COLORS: Record<string, string> = {
  active: 'green', standby: 'yellow', out_of_service: 'red',
};

const EMPTY: Partial<DebrisCrew> = {
  contractorId: '', crewName: '', crewType: '', truckId: '',
  driverName: '', driverPhone: '', driverEmail: '',
  status: 'active', assignedZoneId: '', notes: '',
};

export default function Crews() {
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('debris');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    crews, contractors, trucks, zones, loadingCrews,
    loadCrews, loadContractors, loadTrucks, loadZones,
    createCrew, updateCrew, deleteCrew,
  } = useDebrisStore();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [contractorFilter, setContractorFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DebrisCrew | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DebrisCrew | null>(null);
  const [form, setForm] = useState<Partial<DebrisCrew>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadCrews(); loadContractors(); loadTrucks(); loadZones(); }, [loadCrews, loadContractors, loadTrucks, loadZones]);

  const iCrews = incidentId ? crews : [];

  // Build driver dropdown from existing crew records
  const knownDrivers = useMemo(() => {
    const map = new Map<string, { name: string; phone: string; email: string }>();
    crews.forEach(c => {
      if (c.driverName?.trim()) {
        map.set(c.driverName, { name: c.driverName, phone: c.driverPhone || '', email: c.driverEmail || '' });
      }
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [crews]);
  const driverSelectData = useMemo(() => knownDrivers.map(d => ({ value: d.name, label: d.name })), [knownDrivers]);
  const iContractors = incidentId ? contractors : [];
  const iTrucks = incidentId ? trucks : [];
  const iZones = incidentId ? zones : [];

  const contractorSelect = useMemo(() =>
    iContractors.map(c => ({ value: String(c.id), label: c.companyName || `#${c.id}` })),
  [iContractors]);

  const truckSelect = useMemo(() =>
    iTrucks.map(t => ({ value: String(t.id), label: `${t.truckNumber} (${t.truckType || 'N/A'})` })),
  [iTrucks]);

  const zoneSelect = useMemo(() =>
    iZones.map(z => ({ value: String(z.id), label: z.zoneName || z.zoneCode || `#${z.id}` })),
  [iZones]);

  const filtered = useMemo(() => {
    let list = iCrews;
    if (statusFilter && statusFilter !== 'all') list = list.filter(c => c.status === statusFilter);
    if (contractorFilter && contractorFilter !== 'all') list = list.filter(c => c.contractorId === contractorFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        c.crewName.toLowerCase().includes(q) || c.driverName.toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => a.crewName.localeCompare(b.crewName));
  }, [iCrews, statusFilter, contractorFilter, search]);

  const contractorName = (id: string) => contractors.find(c => String(c.id) === id)?.companyName ?? id;
  const zoneName = (id: string) => zones.find(z => String(z.id) === id)?.zoneName ?? id;
  const truckLabel = (id: string) => trucks.find(t => String(t.id) === id)?.truckNumber ?? id;
  const setField = (key: keyof DebrisCrew, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => { setEditTarget(null); setForm({ ...EMPTY }); setModalOpen(true); };
  const openEdit = (c: DebrisCrew) => { setEditTarget(c); setForm({ ...c }); setModalOpen(true); };

  const handleSave = async () => {
    if (!form.crewName?.trim()) {
      notifications.show({ message: 'Crew name is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, incidentId };
      if (editTarget) {
        await updateCrew(editTarget.id, payload);
        notifications.show({ message: 'Crew updated', color: 'green' });
      } else {
        await createCrew(payload);
        notifications.show({ message: 'Crew created', color: 'green' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteCrew(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.crewName} deleted`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Crews</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingCrews} onClick={() => loadCrews()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Crew</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search crew name, driver..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.currentTarget.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
        <Select data={[{ value: 'all', label: 'All Contractors' }, ...contractorSelect]}
          value={contractorFilter ?? 'all'} onChange={v => setContractorFilter(v)} size="xs" w={200} searchable />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} crew{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Crew</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Type</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Contractor</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Driver</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Truck</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Zone</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingCrews ? 'Loading...' : 'No crews found'}
                </td></tr>
              )}
              {filtered.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>{c.crewName || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    {CREW_TYPES.find(ct => ct.value === c.crewType)?.label ?? c.crewType ?? '-'}
                  </td>
                  <td style={{ padding: '8px 12px' }}>{c.contractorId ? contractorName(c.contractorId) : '-'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <Text size="sm">{c.driverName || '-'}</Text>
                    <Text size="xs" c="dimmed">{c.driverPhone}</Text>
                  </td>
                  <td style={{ padding: '8px 12px' }}>{c.truckId ? truckLabel(c.truckId) : '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{c.assignedZoneId ? zoneName(c.assignedZoneId) : '-'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <Badge size="sm" color={STATUS_COLORS[c.status] ?? 'gray'}>{c.status?.replace(/_/g, ' ')}</Badge>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    <Group gap={4} justify="flex-end">
                      {canEdit && <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(c)}>
                        <IconPencil size={14} />
                      </ActionIcon>}
                      {canEdit && <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(c)}>
                        <IconTrash size={14} />
                      </ActionIcon>}
                    </Group>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Crew' : 'Add Crew'} centered size="lg">
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Crew Name" required value={form.crewName ?? ''}
              onChange={e => setField('crewName', e.currentTarget.value)} />
            <Select label="Crew Type" data={CREW_TYPES} value={form.crewType || null}
              onChange={v => setField('crewType', v ?? '')} clearable />
            <Select label="Contractor" data={contractorSelect} value={form.contractorId || null}
              onChange={v => setField('contractorId', v ?? '')} searchable clearable />
            <Select label="Assigned Truck" data={truckSelect} value={form.truckId || null}
              onChange={v => setField('truckId', v ?? '')} searchable clearable />
            <Select label="Assigned Zone" data={zoneSelect} value={form.assignedZoneId || null}
              onChange={v => setField('assignedZoneId', v ?? '')} searchable clearable />
            <Select label="Status" data={STATUS_OPTIONS}
              value={(form.status as CrewStatus) ?? 'active'}
              onChange={v => setField('status', v ?? 'active')} />
          </SimpleGrid>

          <Text size="sm" fw={600} c="dimmed">Driver Info</Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <Select label="Driver Name" data={driverSelectData} value={form.driverName || null}
              onChange={v => {
                setField('driverName', v ?? '');
                const d = knownDrivers.find(d => d.name === v);
                if (d) { setField('driverPhone', d.phone); setField('driverEmail', d.email); }
              }}
              searchable clearable allowDeselect
              nothingFoundMessage="Type a new driver name above"
            />
            <TextInput label="Driver Phone" value={form.driverPhone ?? ''}
              onChange={e => setField('driverPhone', e.currentTarget.value)} />
            <TextInput label="Driver Email" value={form.driverEmail ?? ''}
              onChange={e => setField('driverEmail', e.currentTarget.value)} />
          </SimpleGrid>

          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.currentTarget.value)} autosize minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Crew'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Crew" centered size="sm">
        <Stack>
          <Text size="sm">Delete <strong>{deleteTarget?.crewName}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
